import React, { useState, useEffect, useRef } from 'react';
import { Form, Input, Select, Button, Space, DatePicker, Table, InputNumber, message, Alert } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { SupplierOrder, Supplier, OrderStatus, Product, CustomerOrder } from '../types.ts';
import { productAPI, customerOrderAPI } from '../services/api.ts';
import { formatCurrency, inputNumberFormatter, inputNumberParser } from '../utils/numberFormatting.ts';
import { getSelectableSupplierOrderStatusIds } from '../utils/supplierOrderStatus.ts';

const { Option } = Select;
const { TextArea } = Input;

interface SupplierOrderFormProps {
  initialValues?: SupplierOrder | null;
  suppliers: Supplier[];
  orderStatuses: OrderStatus[];
  onSubmit: (values: any) => void;
  onCancel: () => void;
}

const SupplierOrderForm: React.FC<SupplierOrderFormProps> = ({
  initialValues,
  suppliers,
  orderStatuses,
  onSubmit,
  onCancel,
}) => {
  const [form] = Form.useForm();
  const [products, setProducts] = useState<Product[]>([]);
  const [customerOrders, setCustomerOrders] = useState<CustomerOrder[]>([]);
  // Ordine cliente di testata al momento dell'ultimo cambio: serve a riallineare le sole
  // righe che lo seguivano come valore predefinito (v3.0.0, destinazione per riga)
  const prevHeaderOrderRef = useRef<number | null>(null);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [orderDetails, setOrderDetails] = useState<any[]>([]);

  // Se lo stock è già stato caricato in produzione (in modifica, stato >= 5), le righe
  // dell'ordine non sono più modificabili (coerente con la guardia backend): si possono
  // modificare solo i campi di testata.
  const stockLoaded = !!initialValues && (initialValues.order_status_id ?? 0) >= 5;

  useEffect(() => {
    loadProducts();
    loadCustomerOrders();
  }, []);

  useEffect(() => {
    if (initialValues) {
      const formData = {
        ...initialValues,
        order_date: dayjs(initialValues.order_date),
        requested_delivery_date: initialValues.delivery_date ? dayjs(initialValues.delivery_date) : null,
        customer_order_id: initialValues.customer_order_id || null,
        stock_code: initialValues.stock_code || null,
      };
      form.setFieldsValue(formData);
      
      // Mappa i dettagli ordine dal backend al formato del form
      const mappedDetails = (initialValues.order_details || []).map(detail => ({
        product_id: detail.product_id,
        quantity: detail.quantity,
        unit_price: detail.cost,
        total_price: detail.quantity * detail.cost,
        notes: detail.notes || '',
        customer_order_id: detail.customer_order_id ?? null,
      }));
      
      setOrderDetails(mappedDetails);
      prevHeaderOrderRef.current = initialValues.customer_order_id ?? null;
    }
  }, [initialValues, form]);

  // Effetto separato per aggiornare il campo customer_order_id dopo il caricamento degli ordini
  useEffect(() => {
    if (initialValues?.customer_order_id && customerOrders.length > 0) {
      form.setFieldValue('customer_order_id', initialValues.customer_order_id);
    }
  }, [customerOrders, initialValues?.customer_order_id, form]);

  const loadProducts = async () => {
    try {
      const data = await productAPI.getProducts();
      setProducts(data);
    } catch (error) {
      message.error('Errore nel caricamento dei prodotti');
    }
  };

  const loadCustomerOrders = async () => {
    setLoadingOrders(true);
    try {
      // Ordini cliente collegabili: solo quelli ancora aperti (stati 1-5, da Nuovo a In
      // Produzione); esclusi gli ordini in evasione (6-9), Evasi (10) e Annullati (99).
      // Il limite alto evita il troncamento ai 100 ordini più recenti del backend.
      const data = await customerOrderAPI.getCustomerOrders({ exclude_completed: true, limit: 1000 });
      const activeOrders = data.filter(order => [1, 2, 3, 4, 5].includes(order.order_status_id));

      // In modifica l'ordine già collegato deve restare selezionabile anche se non più aperto
      const linkedId = initialValues?.customer_order_id;
      if (linkedId && !activeOrders.some(order => order.id === linkedId)) {
        const linkedOrder = data.find(order => order.id === linkedId)
          || await customerOrderAPI.getCustomerOrder(linkedId).catch(() => null);
        if (linkedOrder) {
          activeOrders.push(linkedOrder);
        }
      }
      
      setCustomerOrders(activeOrders);
    } catch (error) {
      console.error('Errore nel caricamento degli ordini cliente:', error);
      message.error('Errore nel caricamento degli ordini cliente');
    } finally {
      setLoadingOrders(false);
    }
  };

  const handleSubmit = (values: any) => {
    if (orderDetails.length === 0) {
      message.error('Aggiungi almeno un prodotto all\'ordine');
      return;
    }
    if (!stockLoaded) {
      const invalidRow = orderDetails.findIndex(d => !d.product_id || !(d.quantity >= 1));
      if (invalidRow >= 0) {
        message.error(`Riga ${invalidRow + 1}: selezionare un prodotto e indicare una quantità di almeno 1`);
        return;
      }
    }

    const formattedValues: any = {
      ...values,
      order_date: values.order_date.format('YYYY-MM-DD'),
      delivery_date: values.requested_delivery_date ? values.requested_delivery_date.format('YYYY-MM-DD') : null,
    };

    // Se lo stock è già caricato, NON inviare le righe (read-only lato backend): si
    // aggiornano solo i campi di testata. Altrimenti invia le righe come di consueto.
    if (!stockLoaded) {
      formattedValues.order_details = orderDetails.map(detail => ({
        product_id: detail.product_id,
        quantity: detail.quantity,
        cost: detail.unit_price,
        notes: detail.notes,
        customer_order_id: detail.customer_order_id ?? null,
      }));
    }

    onSubmit(formattedValues);
  };

  const addOrderDetail = () => {
    setOrderDetails([
      ...orderDetails,
      {
        product_id: null,
        quantity: 1,
        unit_price: 0,
        total_price: 0,
        notes: '',
        customer_order_id: form.getFieldValue('customer_order_id') ?? null,
      },
    ]);
  };

  // Cambio dell'ordine cliente di testata: le righe che avevano il vecchio valore
  // predefinito seguono il nuovo, quelle con una destinazione diversa restano com'erano
  const handleHeaderOrderChange = (value: number | undefined) => {
    const prev = prevHeaderOrderRef.current;
    const next = value ?? null;
    setOrderDetails((rows) => rows.map((d) => ((d.customer_order_id ?? null) === prev ? { ...d, customer_order_id: next } : d)));
    prevHeaderOrderRef.current = next;
  };

  const removeOrderDetail = (index: number) => {
    const newDetails = orderDetails.filter((_, i) => i !== index);
    setOrderDetails(newDetails);
  };

  const updateOrderDetail = (index: number, field: string, value: any) => {
    const newDetails = [...orderDetails];
    newDetails[index][field] = value;

    // Calculate total price when quantity or unit price changes
    if (field === 'quantity' || field === 'unit_price') {
      newDetails[index].total_price = (newDetails[index].quantity || 0) * (newDetails[index].unit_price || 0);
    }

    // Auto-fill price from product when product is selected
    if (field === 'product_id' && value) {
      const product = products.find(p => p.id === value);
      if (product && product.cost) {
        newDetails[index].unit_price = parseFloat(product.cost.toString());
        newDetails[index].total_price = (newDetails[index].quantity || 0) * newDetails[index].unit_price;
      }
    }

    setOrderDetails(newDetails);
  };

  const handleSupplierChange = (supplierId: number) => {
    form.setFieldsValue({ supplier_id: supplierId });
  };

  // Genera identificatore composto per prodotto
  const generateCompositeIdentifier = (internal_code: string, size?: string, color?: string) => {
    if (!size && !color) {
      return internal_code;
    } else if (size && !color) {
      return `${internal_code}-${size}`;
    } else if (!size && color) {
      return `${internal_code}--${color}`;
    } else {
      return `${internal_code}-${size}-${color}`;
    }
  };

  const detailColumns = [
    {
      title: 'Prodotto',
      dataIndex: 'product_id',
      width: '26%',
      render: (productId: number, record: any, index: number) => (
        <Select
          style={{ width: '100%' }}
          placeholder="Seleziona Prodotto"
          value={productId}
          onChange={(value) => updateOrderDetail(index, 'product_id', value)}
          disabled={stockLoaded}
          showSearch
          optionFilterProp="children"
          allowClear
        >
          {products.map((product) => (
            <Option key={product.id} value={product.id}>
              {generateCompositeIdentifier(product.internal_product_code, product.size, product.color)} - {product.product_description}
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: 'Ordine Cliente',
      dataIndex: 'customer_order_id',
      width: '18%',
      render: (customerOrderId: number | null, record: any, index: number) => (
        <Select
          style={{ width: '100%' }}
          placeholder="Nessuna destinazione"
          value={customerOrderId ?? undefined}
          onChange={(value) => updateOrderDetail(index, 'customer_order_id', value ?? null)}
          disabled={stockLoaded}
          showSearch
          optionFilterProp="children"
          allowClear
          loading={loadingOrders}
        >
          {customerOrders.map((order) => (
            <Option key={order.id} value={order.id}>
              {order.order_number} - {order.customer?.company}
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: 'Quantità',
      dataIndex: 'quantity',
      width: '10%',
      render: (quantity: number, record: any, index: number) => (
        <InputNumber
          min={1}
          value={quantity}
          onChange={(value) => updateOrderDetail(index, 'quantity', value || 1)}
          disabled={stockLoaded}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Prezzo Unitario',
      dataIndex: 'unit_price',
      width: '16%',
      render: (price: number, record: any, index: number) => (
        <InputNumber
          min={0}
          precision={4}
          step={0.0001}
          value={price}
          onChange={(value) => updateOrderDetail(index, 'unit_price', value || 0)}
          disabled={stockLoaded}
          formatter={inputNumberFormatter}
          parser={inputNumberParser}
          placeholder="0,0000"
          addonBefore="€"
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Prezzo Totale',
      dataIndex: 'total_price',
      width: '11%',
      render: (total: number) => `€ ${formatCurrency(total || 0)}`,
    },
    {
      title: 'Note',
      dataIndex: 'notes',
      width: '14%',
      render: (notes: string, record: any, index: number) => (
        <Input
          value={notes}
          onChange={(e) => updateOrderDetail(index, 'notes', e.target.value)}
          placeholder="Note opzionali"
        />
      ),
    },
    {
      title: 'Azioni',
      width: '5%',
      render: (_: any, record: any, index: number) => (
        <Button
          danger
          size="small"
          icon={<DeleteOutlined />}
          onClick={() => removeOrderDetail(index)}
          disabled={stockLoaded}
        />
      ),
    },
  ];

  const totalAmount = orderDetails.reduce((sum, detail) => sum + (detail.total_price || 0), 0);

  // Stati selezionabili nel dropdown: in creazione gli stati creabili (1..5); in modifica lo
  // stato attuale + le sole transizioni manuali autorizzate (util condiviso col modal "Cambia
  // Stato", coerente col vincolo backend). Gli altri stati NON sono selezionabili.
  const selectableStatusIds = getSelectableSupplierOrderStatusIds(initialValues?.order_status_id);
  const availableStatuses = orderStatuses.filter((status) => selectableStatusIds.includes(status.id));

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={{
        order_date: dayjs(),
        order_status_id: orderStatuses[0]?.id,
      }}
    >
      <Form.Item
        name="order_number"
        label="Numero Ordine"
        rules={[{ required: true, message: 'Inserire il numero ordine' }]}
      >
        <Input 
          placeholder="Numero Ordine"
          style={{ textTransform: 'uppercase' }}
          onChange={(e) => {
            const upperValue = e.target.value.toUpperCase();
            form.setFieldValue('order_number', upperValue);
          }}
        />
      </Form.Item>

      <Form.Item
        name="supplier_id"
        label="Fornitore"
        rules={[{ required: true, message: 'Selezionare un fornitore' }]}
      >
        <Select 
          placeholder="Seleziona Fornitore"
          onChange={handleSupplierChange}
          showSearch
          optionFilterProp="children"
          disabled={stockLoaded}
        >
          {suppliers.map((supplier) => (
            <Option key={supplier.id} value={supplier.id}>
              {supplier.name}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        name="customer_order_id"
        label="Ordine Cliente Collegato (opzionale)"
        extra={stockLoaded
          ? 'Non modificabile: lo stock è già stato caricato in produzione'
          : 'Valore predefinito per le righe: ogni riga può avere un ordine cliente diverso (colonna "Ordine Cliente" nella tabella prodotti)'}
      >
        <Select 
          placeholder="Seleziona Ordine Cliente"
          allowClear
          disabled={stockLoaded}
          showSearch
          optionFilterProp="children"
          onChange={handleHeaderOrderChange}
          loading={loadingOrders}
          notFoundContent={loadingOrders ? 'Caricamento...' : 'Nessun ordine disponibile'}
        >
          {customerOrders.map((order) => (
            <Option key={order.id} value={order.id}>
              {order.order_number} - {order.customer?.company} 
              {order.order_date && ` (${new Date(order.order_date).toLocaleDateString('it-IT')})`}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Space style={{ width: '100%' }} size="large">
        <Form.Item
          name="order_date"
          label="Data Ordine"
          rules={[{ required: true, message: 'Inserire la data ordine' }]}
        >
          <DatePicker format="DD/MM/YYYY" />
        </Form.Item>

        <Form.Item
          name="requested_delivery_date"
          label="Data Consegna Fornitore"
        >
          <DatePicker format="DD/MM/YYYY" />
        </Form.Item>

        <Form.Item
          name="order_status_id"
          label="Stato Ordine"
          rules={[{ required: true, message: 'Selezionare uno stato' }]}
        >
          <Select>
            {availableStatuses.map((status) => (
              <Option key={status.id} value={status.id}>
                {status.status_name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="stock_code"
          label="Stock Code"
          extra="Se vuoto verrà generato automaticamente"
        >
          <Input
            placeholder="Codice Stock (opzionale)"
            maxLength={50}
          />
        </Form.Item>
      </Space>

      <Form.Item
        name="notes"
        label="Note"
      >
        <TextArea rows={3} placeholder="Note opzionali" />
      </Form.Item>

      {stockLoaded && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Righe ordine in sola lettura"
          description="Lo stock è già stato caricato in produzione: i prodotti non sono modificabili. È possibile aggiornare solo i campi di testata."
        />
      )}

      <div style={{ marginBottom: 16 }}>
        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onClick={addOrderDetail}
          disabled={stockLoaded}
        >
          Aggiungi Prodotto
        </Button>
      </div>

      <Table
        columns={detailColumns}
        dataSource={orderDetails}
        rowKey={(record, index) => index?.toString() || '0'}
        pagination={false}
        size="small"
        locale={{ emptyText: 'Nessun prodotto aggiunto' }}
        scroll={{ x: 'max-content' }}
      />

      <div style={{ marginTop: 16, textAlign: 'right' }}>
        <strong>
          Totale: € {formatCurrency(totalAmount)}
        </strong>
      </div>

      <Form.Item style={{ marginTop: 24 }}>
        <Space>
          <Button type="primary" htmlType="submit">
            {initialValues ? 'Aggiorna' : 'Crea'}
          </Button>
          <Button onClick={onCancel}>
            Annulla
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
};

export default SupplierOrderForm;